import { type MouseEvent, useEffect, useMemo, useState } from 'react'

import { useLastData } from '@/hooks/useLastData'
import { HiOutlineOfficeBuilding } from 'react-icons/hi'

import { Box, Flex, Skeleton, useDisclosure } from '@chakra-ui/react'

import { ActionButtons } from '@/components/layouts/ActionButtons'
import { AppLayout } from '@/components/layouts/AppLayout'
import { EditModeFrame } from '@/components/layouts/EditMode'
import { Head } from '@/components/layouts/Head'
import { Header } from '@/components/layouts/Header'
import { PageSpinnerToast } from '@/components/ui'
import { PreloadCannedVideos } from '@/features/canned-video'
import { EditFacilityModal } from '@/features/facility-crud'
import { GlobalMap, trackOrganisationInfo } from '@/features/global-map'
import {
  useGlobalFacilitiesQuery,
  useIncidentsByMarketFacilityQuery,
  useOrganizationFacilitiesIncidentCountQuery,
  useOrganizationQuery,
} from '@/graphql/generated/hooks'
import { GlobalFacility } from '@/graphql/generated/schemas'
import { startTimer } from '@/utils/analytics'
import { requireAuth } from '@/utils/auth/requireAuth'
import { GlobalMapProperties, mergeCountsIntoFacilities } from '@/utils/helpers'

export const GLOBAL_PAGE_LOAD_ANALYTIC_NAME = 'Page Load: Global Map View'

const Global = () => {
  const [facilityId, setFacilityId] = useState(null)
  const {
    isOpen: isEditFacilityOpen,
    onOpen: onEditFacilityOpen,
    onClose: onEditFacilityClose,
  } = useDisclosure()

  const { data: orgData } = useOrganizationQuery({
    fetchPolicy: 'cache-and-network',
  })
  const { data: globalFacilitiesData, loading: areFacilitiesLoading } =
    useGlobalFacilitiesQuery({
      fetchPolicy: 'cache-and-network',
    })
  const {
    data: orgFacilitiesIncidentCount,
    loading: areFacilityIncidentsLoading,
  } = useOrganizationFacilitiesIncidentCountQuery({
    fetchPolicy: 'cache-and-network',
  })
  const { data: marketFacilityIncidents } = useIncidentsByMarketFacilityQuery({
    fetchPolicy: 'network-only',
    pollInterval: 60_000,
  })

  const allFacilitiesWithoutIncidents: GlobalFacility[] =
    globalFacilitiesData?.globalFacilities?.edges?.map((el) => el.node)

  const mapOfFacilitiesWithIncidents = Object.fromEntries(
    orgFacilitiesIncidentCount?.activeIncidentCountsForAffectedFacilities?.edges.map(
      (e) => [e.node.id, e.node.incidents.totalCount]
    ) ?? []
  )

  const thingsToShowOnMap: GlobalMapProperties[] = useMemo(() => {
    return mergeCountsIntoFacilities(
      allFacilitiesWithoutIncidents,
      mapOfFacilitiesWithIncidents
    )
  }, [allFacilitiesWithoutIncidents, mapOfFacilitiesWithIncidents])

  const lastData: GlobalMapProperties[] = useLastData(thingsToShowOnMap)

  useEffect(() => {
    startTimer(GLOBAL_PAGE_LOAD_ANALYTIC_NAME)
  }, [])

  useEffect(() => {
    if (!areFacilitiesLoading && !areFacilityIncidentsLoading) {
      trackOrganisationInfo(
        globalFacilitiesData?.globalFacilities.totalCount,
        orgFacilitiesIncidentCount
      )
    }
  }, [areFacilitiesLoading, areFacilityIncidentsLoading])

  const openEditFacility = (
    id: string,
    e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>
  ) => {
    e.stopPropagation()
    setFacilityId(id)
    onEditFacilityOpen()
  }

  return (
    <>
      <Head metaSubTitle='Global' />
      <PreloadCannedVideos />
      <AppLayout>
        <Box>
          <Header
            dataTestId='globalPage_header'
            left={
              <Flex alignItems='center' paddingLeft='6'>
                <Box paddingRight='3'>
                  <HiOutlineOfficeBuilding color='#000' size={24} />
                </Box>
                {orgData ? (
                  <Box fontSize='20px' fontWeight='extrabold'>
                    {orgData?.currentOrganization?.name}
                  </Box>
                ) : (
                  <Skeleton h='24px' w='90px' />
                )}
              </Flex>
            }
            right={<ActionButtons />}
          />
          <Box h='calc(100vh - 65px)' pos='relative' w='full'>
            <EditModeFrame />
            <PageSpinnerToast
              loading={areFacilitiesLoading || areFacilityIncidentsLoading}
            />
            <GlobalMap
              facilitiesData={lastData}
              marketFacilityIncidentsData={marketFacilityIncidents}
              openEditFacility={openEditFacility}
            />
          </Box>
        </Box>
      </AppLayout>

      {isEditFacilityOpen && (
        <EditFacilityModal
          facilityId={facilityId}
          isOpen={isEditFacilityOpen}
          onClose={() => {
            setFacilityId(null)
            onEditFacilityClose()
          }}
        />
      )}
    </>
  )
}

// Default export is needed for NextJS /pages/*
// eslint-disable-next-line import/no-default-export
export default requireAuth(Global)
